/**
 * Contract Subtype Catalog (Issue #1545)
 *
 * The hardcoded `ContractType` Prisma + TS enum was removed in #1545 in favor
 * of a DB-backed `ContractSubtypeTemplate` + `ContractSubtype` taxonomy
 * (CLAUDE.md §2f platform-defaults pattern). This file now ships:
 *
 * 1. `InvitationContractStatus` — runtime lifecycle status of a contract
 *    inside a partner-invitation package. Stays in TS-land (no DB column;
 *    persisted as a free-form `status` string on `InvitationContract`).
 *
 * 2. `PLATFORM_DEFAULT_CONTRACT_SUBTYPES` — the seed-only canonical list of
 *    8 platform-default contract subtypes. Inserted exactly once into
 *    `ContractSubtypeTemplate` at platform install / Prisma migrate. Tenants
 *    fork into per-tenant `ContractSubtype` rows with `templateId` set.
 *    The UI never imports this — subtypes are surfaced via the merged-view
 *    tRPC query `unifiedTemplates.listSubtypes({ kind: 'CONTRACT' })`.
 */

import { z } from 'zod';

// ============================================================================
// PLATFORM DEFAULT CONTRACT SUBTYPES (Issue #1545)
// ============================================================================

/**
 * Group classifier for platform-default contract subtypes. Drives downstream
 * UI behavior such as conditionally rendering commission-terms steps in the
 * partner-invite wizard (financial subtypes only). Kept narrow because the
 * group is a hard-coded property of the platform default — tenants do not
 * override it on forked rows.
 */
export type ContractSubtypeGroup = 'financial' | 'legal';

/**
 * Shape of a platform-default contract subtype. Mirrors the
 * `ContractSubtypeTemplate` Prisma model, minus DB-only fields (`id`,
 * `createdAt`, `updatedAt`, `isActive`). Seeders construct full DB rows from
 * this shape; everything else reads subtypes from the merged-view tRPC query.
 */
export interface PlatformDefaultContractSubtype {
  slug: string;
  name: string;
  description: string;
  icon: string;
  group: ContractSubtypeGroup;
  defaultSortOrder: number;
}

/**
 * Canonical seed list. New platform-wide subtypes are added by appending a
 * row here (and the tenant-provisioning seed will fork it for new
 * businesses). Existing tenants pick up additions on their next provisioning
 * pass; admin UI for per-tenant adoption is a follow-up feature.
 */
export const PLATFORM_DEFAULT_CONTRACT_SUBTYPES: ReadonlyArray<PlatformDefaultContractSubtype> = [
  {
    slug: 'referral_broker',
    name: 'Referral Broker Agreement',
    description: 'Commission-based referral partnership for lead generation.',
    icon: 'TrendingUp',
    group: 'financial',
    defaultSortOrder: 10,
  },
  {
    slug: 'correspondent_lending',
    name: 'Correspondent Lending Agreement',
    description: 'Mortgage-specific loan purchase and underwriting terms.',
    icon: 'AccountBalance',
    group: 'financial',
    defaultSortOrder: 20,
  },
  {
    slug: 'wholesale',
    name: 'Wholesale Agreement',
    description: 'Broker submits deals for wholesale pricing and processing.',
    icon: 'Warehouse',
    group: 'financial',
    defaultSortOrder: 30,
  },
  {
    slug: 'channel_partner',
    name: 'Channel Partner Agreement',
    description: 'Reseller or white-label integration partnership.',
    icon: 'Storefront',
    group: 'financial',
    defaultSortOrder: 40,
  },
  {
    slug: 'nda',
    name: 'Non-Disclosure Agreement',
    description: 'Mutual or unilateral confidentiality protection.',
    icon: 'Shield',
    group: 'legal',
    defaultSortOrder: 50,
  },
  {
    slug: 'dpa',
    name: 'Data Processing Agreement',
    description: 'Data processing terms for GDPR/CCPA compliance.',
    icon: 'Security',
    group: 'legal',
    defaultSortOrder: 60,
  },
  {
    slug: 'msa',
    name: 'Master Service Agreement',
    description: 'Overarching master terms governing the partnership.',
    icon: 'Description',
    group: 'legal',
    defaultSortOrder: 70,
  },
  {
    slug: 'sla',
    name: 'Service Level Agreement',
    description: 'Service uptime, response time, and penalty commitments.',
    icon: 'Speed',
    group: 'legal',
    defaultSortOrder: 80,
  },
];

/**
 * Slugs of the platform-default subtypes that carry financial / commission
 * implications. Used by the partner-invite wizard to gate the commission
 * terms step on whether any selected contract is financial. Per-tenant
 * forks inherit this classification — overrides cannot change `group`.
 */
export const FINANCIAL_CONTRACT_SUBTYPE_SLUGS: ReadonlySet<string> = new Set(
  PLATFORM_DEFAULT_CONTRACT_SUBTYPES.filter((s) => s.group === 'financial').map((s) => s.slug),
);

// ============================================================================
// INVITATION CONTRACT STATUS
// ============================================================================

/**
 * Lifecycle status of an individual contract within an invitation package.
 * Persisted as a free-form `status` string on `InvitationContract` rows.
 */
export enum InvitationContractStatus {
  DRAFT = 'draft',
  PENDING_SIGNATURE = 'pending_signature',
  B1_SIGNED = 'b1_signed',
  B2_SIGNED = 'b2_signed',
  COMPLETED = 'completed',
  VOIDED = 'voided',
}

/** Zod schema for InvitationContractStatus validation */
export const InvitationContractStatusSchema = z.nativeEnum(InvitationContractStatus);

/** Array of all invitation contract statuses for iteration */
export const INVITATION_CONTRACT_STATUSES = Object.values(InvitationContractStatus);
